import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/Badge';
import { PARTICIPANT_STATUS_BADGE_TONE, ROLE_BADGE_TONE } from '@/components/ui/Badge/tones';
import CustomSelect, { type SelectOption } from '@/components/ui/CustomSelect';
import { ListPagination } from '@/components/ui/ListPagination';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { TOURNAMENT_ROLES, PARTICIPANT_STATUSES } from '@/constants';
import styles from './styles.module.scss';

// Types
export interface TournamentParticipant {
  id: number;
  tournament_id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  username: string | null;
  email: string;
  role: 'holder' | 'maintainer' | 'participant';
  status: 'active' | 'blocked' | 'awaiting';
  joined_at: string;
  last_activity_at: string | null;
  total_predictions: number;
  total_points: number;
  blocked_at: string | null;
  block_reason: string | null;
  blocked_by_first_name: string | null;
  blocked_by_last_name: string | null;
  days_since_last_activity: number | null;
  leaderboard_position: number;
  invite_code: string | null;
}

export type StatusFilter = 'all' | 'active' | 'blocked' | 'awaiting';
export type RoleFilter = 'all' | 'holder' | 'maintainer' | 'participant';
export type SortField = 'joined_at' | 'last_activity_at';
export type SortOrder = 'asc' | 'desc';

export interface ParticipantsTableProps {
  participants: TournamentParticipant[];
  currentUserId?: number;
  sortBy: SortField;
  sortOrder: SortOrder;
  onSortChange: (field: SortField, order: SortOrder) => void;
  statusFilter: StatusFilter;
  roleFilter: RoleFilter;
  onStatusFilterChange: (status: StatusFilter) => void;
  onRoleFilterChange: (role: RoleFilter) => void;
  onBlockToggle?: (participant: TournamentParticipant) => void;
  onRevokeInvite?: (participant: TournamentParticipant) => void;
  onDeleteParticipant?: (participant: TournamentParticipant) => void;
  onRoleChange?: (participant: TournamentParticipant, newRole: 'participant' | 'maintainer') => void;
  actionLoading?: number | null;
  emptyMessage?: string;
  className?: string;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];

const ChevronDownIcon: React.FC = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UsersIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3.5 18c.5-2.9 2.7-4.7 5.5-4.7s5 1.8 5.5 4.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="16.5" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M14 13.2c.7-.3 1.6-.5 2.5-.5 2 0 3.5 1.1 4 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const getInitials = (p: TournamentParticipant): string => {
  const f = (p.first_name || '').trim();
  const l = (p.last_name || '').trim();
  if (f && l) return (f[0] + l[0]).toUpperCase();
  if (f) return f.slice(0, 2).toUpperCase();
  if (p.username) return p.username.slice(0, 2).toUpperCase();
  return '?';
};

const activityClass = (days: number | null): string => {
  if (days === null) return '';
  if (days <= 1) return styles.recent;
  if (days <= 7) return styles.stale;
  return styles.cold;
};

export const ParticipantsTable: React.FC<ParticipantsTableProps> = ({
  participants,
  currentUserId,
  sortBy,
  sortOrder,
  onSortChange,
  statusFilter,
  roleFilter,
  onStatusFilterChange,
  onRoleFilterChange,
  onBlockToggle,
  onRevokeInvite,
  onDeleteParticipant,
  onRoleChange,
  actionLoading: _actionLoading,
  emptyMessage,
  className,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const isMobile = useBreakpointDown('lg');
  const { t } = useTranslation(['tournament', 'common']);

  const STATUS_OPTIONS = useMemo<SelectOption[]>(() => [
    { value: 'all', label: t('tournament:allStatuses') },
    { value: PARTICIPANT_STATUSES.ACTIVE, label: t('tournament:active') },
    { value: PARTICIPANT_STATUSES.BLOCKED, label: t('tournament:blocked') },
    { value: PARTICIPANT_STATUSES.AWAITING, label: t('tournament:awaiting') },
  ], [t]);

  const ROLE_FILTER_OPTIONS = useMemo<SelectOption[]>(() => [
    { value: 'all', label: t('tournament:allRoles') },
    { value: TOURNAMENT_ROLES.HOLDER, label: t('tournament:holders') },
    { value: TOURNAMENT_ROLES.MAINTAINER, label: t('tournament:maintainers') },
    { value: TOURNAMENT_ROLES.PARTICIPANT, label: t('tournament:participantsRole') },
  ], [t]);

  const ROLE_CHANGE_OPTIONS = useMemo<SelectOption[]>(() => [
    { value: TOURNAMENT_ROLES.PARTICIPANT, label: t('tournament:participantRole') },
    { value: TOURNAMENT_ROLES.MAINTAINER, label: t('tournament:maintainerRole') },
  ], [t]);

  const formatLastActivity = (lastActivity: string | null, daysAgo: number | null): string => {
    if (!lastActivity || daysAgo === null) return t('common:never');
    if (daysAgo === 0) return t('common:today');
    if (daysAgo === 1) return t('common:yesterday');
    return t('common:daysAgo', { count: daysAgo });
  };

  const statusValue = useMemo(
    () => STATUS_OPTIONS.find((o) => o.value === statusFilter) || STATUS_OPTIONS[0],
    [statusFilter, STATUS_OPTIONS],
  );
  const roleFilterValue = useMemo(
    () => ROLE_FILTER_OPTIONS.find((o) => o.value === roleFilter) || ROLE_FILTER_OPTIONS[0],
    [roleFilter, ROLE_FILTER_OPTIONS],
  );

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      onSortChange(field, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(field, 'asc');
    }
    onPageChange(0);
  };

  const handleRowsPerPageChange = (value: number) => {
    onRowsPerPageChange(value);
    onPageChange(0);
  };

  const renderSortHeader = (field: SortField, label: string, colClass: string) => {
    const isActive = sortBy === field;
    return (
      <span className={colClass}>
        <span
          className={`${styles.sortableHeader} ${isActive ? styles.activeSort : ''}`}
          onClick={() => handleSort(field)}
        >
          {label}
          {isActive && (
            <span className={styles.sortArrow}>
              {sortOrder === 'asc' ? '▲' : '▼'}
            </span>
          )}
        </span>
      </span>
    );
  };

  const renderRoleCell = (participant: TournamentParticipant) => {
    if (onRoleChange && participant.role !== TOURNAMENT_ROLES.HOLDER) {
      const currentValue = ROLE_CHANGE_OPTIONS.find((o) => o.value === participant.role) || ROLE_CHANGE_OPTIONS[0];
      const wrapperColorClass =
        participant.role === TOURNAMENT_ROLES.MAINTAINER ? styles.roleMaintainer : styles.roleParticipant;
      return (
        <span className={`${styles.roleSelectWrapper} ${wrapperColorClass}`}>
          <CustomSelect
            options={ROLE_CHANGE_OPTIONS}
            value={currentValue}
            onChange={(val) => {
              if (val && val.value !== participant.role) {
                onRoleChange(participant, val.value as 'participant' | 'maintainer');
              }
            }}
            variant="bare"
            isSearchable={false}
          />
          <span className={styles.inlineChevron}>
            <ChevronDownIcon />
          </span>
        </span>
      );
    }
    return (
      <Badge tone={ROLE_BADGE_TONE[participant.role]}>{participant.role}</Badge>
    );
  };

  const renderActivity = (participant: TournamentParticipant) => (
    <span className={styles.activity}>
      <span className={`${styles.activityDot} ${activityClass(participant.days_since_last_activity)}`} />
      {formatLastActivity(participant.last_activity_at, participant.days_since_last_activity)}
    </span>
  );

  const fallbackEmpty = emptyMessage ?? t('tournament:noParticipantsFound');

  return (
    <div className={`${styles.card} ${className || ''}`}>
      {/* Filters toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.filterSelectWrapper}>
          <CustomSelect
            options={STATUS_OPTIONS}
            value={statusValue}
            onChange={(val) => {
              if (val) {
                onStatusFilterChange(val.value as StatusFilter);
                onPageChange(0);
              }
            }}
            variant="basic"
            isSearchable={false}
          />
        </div>
        <div className={styles.filterSelectWrapper}>
          <CustomSelect
            options={ROLE_FILTER_OPTIONS}
            value={roleFilterValue}
            onChange={(val) => {
              if (val) {
                onRoleFilterChange(val.value as RoleFilter);
                onPageChange(0);
              }
            }}
            variant="basic"
            isSearchable={false}
          />
        </div>
      </div>

      {/* Table header */}
      <div className={styles.tableHeader}>
        <span className={styles.colName}>{t('tournament:name')}</span>
        <span className={styles.colRole}>{t('tournament:role')}</span>
        <span className={styles.colStatus}>{t('tournament:status')}</span>
        {renderSortHeader('last_activity_at', t('tournament:activity'), styles.colActivity)}
        <span className={styles.colAction}></span>
      </div>

      {/* Table body */}
      <div className={styles.tableBody}>
        {participants.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <UsersIcon />
            </div>
            <p className={styles.emptyStateText}>{fallbackEmpty}</p>
          </div>
        ) : (
          participants.map((participant) => {
            const isCurrentUser = participant.user_id === currentUserId;

            return (
              <React.Fragment key={participant.id ?? `invite-${participant.invite_code ?? participant.email}`}>
                <div className={`${styles.tableRow} ${isCurrentUser ? styles.currentUser : ''}`}>
                  {/* Name + avatar */}
                  <span className={styles.colName}>
                    <span className={styles.avatar} aria-hidden="true">
                      {getInitials(participant)}
                    </span>
                    <div className={styles.nameContent}>
                      <span className={styles.participantName}>
                        {participant.first_name} {participant.last_name}
                      </span>
                      {participant.username && (
                        <span className={styles.participantUsername}>@{participant.username}</span>
                      )}
                      {isMobile && (
                        <div className={styles.mobileBadges}>
                          {renderRoleCell(participant)}
                          <Badge tone={PARTICIPANT_STATUS_BADGE_TONE[participant.status]}>{participant.status}</Badge>
                          <span className={styles.mobileMeta}>
                            <span className={`${styles.activityDot} ${activityClass(participant.days_since_last_activity)}`} />
                            {formatLastActivity(participant.last_activity_at, participant.days_since_last_activity)}
                          </span>
                        </div>
                      )}
                    </div>
                  </span>

                  {/* Role — desktop */}
                  <span className={styles.colRole}>
                    {renderRoleCell(participant)}
                  </span>

                  {/* Status — desktop */}
                  <span className={styles.colStatus}>
                    <Badge tone={PARTICIPANT_STATUS_BADGE_TONE[participant.status]}>{participant.status}</Badge>
                  </span>

                  {/* Activity — desktop */}
                  <span className={styles.colActivity}>
                    {renderActivity(participant)}
                  </span>

                  {/* Action */}
                  <span className={styles.colAction}>
                    {participant.role !== TOURNAMENT_ROLES.HOLDER && participant.status === PARTICIPANT_STATUSES.AWAITING && onRevokeInvite && (
                      <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                        aria-label="Revoke invitation"
                        onClick={() => onRevokeInvite(participant)}
                      >
                        <i className="icon-cross" aria-hidden="true" />
                      </button>
                    )}
                    {participant.role !== TOURNAMENT_ROLES.HOLDER && participant.status !== PARTICIPANT_STATUSES.AWAITING && onBlockToggle && (
                      <button
                        type="button"
                        className={styles.actionBtn}
                        aria-label={participant.status === PARTICIPANT_STATUSES.BLOCKED ? 'Unblock' : 'Block'}
                        onClick={() => onBlockToggle(participant)}
                      >
                        <i
                          className={participant.status === PARTICIPANT_STATUSES.BLOCKED ? 'icon-check' : 'icon-blocked'}
                          aria-hidden="true"
                        />
                      </button>
                    )}
                    {participant.role !== TOURNAMENT_ROLES.HOLDER && participant.status !== PARTICIPANT_STATUSES.AWAITING && onDeleteParticipant && (
                      <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                        aria-label="Remove participant"
                        onClick={() => onDeleteParticipant(participant)}
                      >
                        <i className="icon-trash" aria-hidden="true" />
                      </button>
                    )}
                  </span>
                </div>

                {/* Block reason sub-row */}
                {participant.status === PARTICIPANT_STATUSES.BLOCKED && participant.block_reason && (
                  <div className={styles.blockReasonRow}>
                    <strong className={styles.blockReasonLabel}>{t('tournament:reason').replace(/:$/, '')}</strong>
                    <span>{participant.block_reason}</span>
                  </div>
                )}
              </React.Fragment>
            );
          })
        )}
      </div>

      <ListPagination
        page={page}
        pageSize={rowsPerPage}
        total={totalCount}
        pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={onPageChange}
        onPageSizeChange={handleRowsPerPageChange}
        testIdPrefix="participants"
      />
    </div>
  );
};

export default ParticipantsTable;
